import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Building2, CreditCard, FileText, Files, Receipt, Gift, Home, LogOut, MessageCircle, MoreHorizontal, Plug, ShieldAlert, Wrench, type LucideIcon } from 'lucide-react';
import { BrandMark, useBrand } from '@cover/core/brand.tsx';
import { useChat } from './components/Chat.tsx';
import { cn } from '@cover/core/cn.ts';
import { displayPhone } from '@cover/core/format.ts';
import { Dialog } from '@cover/core/ui/Dialog.tsx';
import { signOut, useSession } from './session.tsx';
import { useCustomerLive } from './live.ts';
import { liveLabel, type LiveStatus } from '@cover/core/live.ts';

type Item = { to: string; label: string; icon: LucideIcon };
const NAV: Item[] = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/business', label: 'My business', icon: Building2 },
  { to: '/quotes', label: 'Quotes', icon: Receipt },
  { to: '/policies', label: 'Policies', icon: FileText },
  { to: '/documents', label: 'Documents', icon: Files },
  { to: '/equipment', label: 'Equipment', icon: Wrench },
  { to: '/claims', label: 'Claims', icon: ShieldAlert },
  { to: '/connections', label: 'Connections', icon: Plug },
  { to: '/refer', label: 'Refer a mate', icon: Gift },
  { to: '/account/payment', label: 'Payment method', icon: CreditCard },
];
// Before there's a business, only these make sense.
const NEW_NAV = ['/', '/account/payment'];
// The phone tab bar keeps the four things people open most; the rest sit under More.
const TABS: Item[] = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/business', label: 'Business', icon: Building2 },
  { to: '/equipment', label: 'Equipment', icon: Wrench },
  { to: '/claims', label: 'Claims', icon: ShieldAlert },
];

/** Shows whether the dashboard is updating live from /v1/changes. */
function LiveDot({ status }: { status: LiveStatus }) {
  if (status === 'off') return null;
  const { text, tone } = liveLabel(status);
  return (
    <p className="inline-flex items-center gap-1.5 text-xs text-mid" aria-live="polite">
      <span className={cn('h-2 w-2 rounded-full', tone === 'ok' ? 'bg-ok-fg' : tone === 'warn' ? 'bg-warn-fg' : 'bg-muted')} aria-hidden />
      {text}
    </p>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const live = useCustomerLive();
  const brand = useBrand();
  const { me, businessId } = useSession();
  const nav = businessId ? NAV : NAV.filter((n) => NEW_NAV.includes(n.to));
  const tabs = businessId ? TABS : TABS.filter((n) => NEW_NAV.includes(n.to));
  const [more, setMore] = useState(false);
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const chat = useChat();

  // Move focus to the new page's heading on navigation, for screen readers.
  const first = useRef(true);
  useEffect(() => {
    setMore(false);
    if (first.current) {
      first.current = false;
      return;
    }
    mainRef.current?.querySelector<HTMLElement>('h1')?.focus();
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-dvh lg:flex">
      <a href="#main" className="sr-only z-50 rounded bg-white px-3 py-2 focus:not-sr-only focus:fixed focus:left-2 focus:top-2">
        Skip to content
      </a>

      <aside className="sticky top-0 hidden h-dvh w-72 shrink-0 flex-col p-4 lg:flex">
        <div className="flex h-full flex-col rounded-3xl bg-white shadow-card ring-1 ring-line">
          <div className="px-5 pb-4 pt-6">
            <BrandMark />
          </div>
          <nav aria-label="Main" className="flex-1 overflow-y-auto px-3">
            <ul className="space-y-0.5">
              {nav.map((n) => (
                <li key={n.to}>
                  <NavLink
                    end={n.to === '/'}
                    to={n.to}
                    className={({ isActive }) =>
                      cn('flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-mid no-underline transition-colors hover:bg-surface hover:text-ink hover:no-underline', isActive && 'bg-brand/10 text-brand hover:bg-brand/10 hover:text-brand')
                    }
                  >
                    <n.icon className="h-5 w-5" aria-hidden />
                    {n.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          <div className="space-y-3 p-3">
            <button type="button" onClick={() => chat.open()} className="flex w-full items-center gap-3 rounded-2xl bg-brand px-4 py-3 font-semibold text-on-brand hover:brightness-110">
              <MessageCircle className="h-5 w-5" aria-hidden /> Message us
            </button>
            <div className="flex items-center gap-3 rounded-2xl bg-surface px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/15 text-sm font-bold text-brand" aria-hidden>
                {(me.contact?.displayName ?? '?').slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{me.contact?.displayName ?? (displayPhone(me.contact?.phoneMasked) ?? 'Signed in')}</p>
                <LiveDot status={live} />
              </div>
              <button onClick={() => signOut()} className="flex h-11 w-11 items-center justify-center rounded-xl text-mid hover:bg-white hover:text-ink" aria-label="Sign out">
                <LogOut className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-line/60 bg-white/85 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] backdrop-blur-md lg:hidden">
          <BrandMark />
          <button type="button" onClick={() => chat.open()} className="flex min-h-11 items-center gap-1.5 rounded-full bg-brand px-4 text-sm font-semibold text-on-brand">
            <MessageCircle className="h-4 w-4" aria-hidden /> Chat
          </button>
        </header>

        <main id="main" ref={mainRef} className="w-full max-w-6xl flex-1 px-4 pb-28 pt-6 sm:px-6 lg:px-10 lg:pb-12 lg:pt-10">
          {children}
        </main>

        {brand.regulatoryLine && <p className="mb-24 max-w-6xl px-4 text-center text-xs text-muted sm:px-6 lg:mb-6 lg:px-10 lg:text-left">{brand.regulatoryLine}</p>}

        <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-30 border-t border-line/60 bg-white/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden">
          <ul className="grid grid-cols-5">
            {tabs.map((n) => (
              <li key={n.to}>
                <NavLink end={n.to === '/'} to={n.to} className={({ isActive }) => cn('group flex min-h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted no-underline hover:no-underline', isActive && 'text-brand')}>
                  {({ isActive }) => (
                    <>
                      <span className={cn('flex h-8 w-14 items-center justify-center rounded-full transition-colors', isActive && 'bg-brand/10')}>
                        <n.icon className="h-5 w-5" aria-hidden />
                      </span>
                      {n.label}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
            <li>
              <button onClick={() => setMore(true)} className="flex min-h-16 w-full flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted" aria-haspopup="dialog">
                <span className="flex h-8 w-14 items-center justify-center rounded-full">
                  <MoreHorizontal className="h-5 w-5" aria-hidden />
                </span>
                More
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <Dialog open={more} onOpenChange={setMore} title="More">
        <ul className="-mx-2 space-y-1">
          {nav.filter((n) => !tabs.some((t) => t.to === n.to)).map((n) => (
            <li key={n.to}>
              <NavLink to={n.to} className="flex items-center gap-3 rounded-md px-3 py-3 font-medium text-ink no-underline hover:bg-canvas hover:no-underline">
                <n.icon className="h-5 w-5 text-mid" aria-hidden />
                {n.label}
              </NavLink>
            </li>
          ))}
          <li>
            <button onClick={() => signOut()} className="flex w-full items-center gap-3 rounded-md px-3 py-3 font-medium text-ink hover:bg-canvas">
              <LogOut className="h-5 w-5 text-mid" aria-hidden /> Sign out
            </button>
          </li>
        </ul>
      </Dialog>
    </div>
  );
}

/** Page title: sets document.title and is the focus target after navigation. */
export function PageTitle({ children, lead }: { children: string; lead?: ReactNode }) {
  const brand = useBrand();
  useEffect(() => {
    document.title = `${children} · ${brand.name}`;
  }, [children, brand.name]);
  return (
    <div className="mb-6 lg:mb-8">
      <h1 tabIndex={-1} className="text-[28px] font-bold leading-tight tracking-tight text-ink focus:outline-none sm:text-4xl">
        {children}
      </h1>
      {lead && <p className="mt-2 max-w-prose text-[15px] text-mid sm:text-base">{lead}</p>}
    </div>
  );
}
